export type DrawingHistoryAction = "create" | "delete" | "update";

export interface DrawingHistoryEntry<T = unknown> {
  type: DrawingHistoryAction;
  drawingId: string;
  before?: T | null;
  after?: T | null;
  timestamp: number;
}

export interface DrawingHistoryState<T = unknown> {
  undoStack: DrawingHistoryEntry<T>[];
  redoStack: DrawingHistoryEntry<T>[];
  limit: number;
  suppressed: boolean;
}

export interface DrawingHistoryMutation<T = unknown> {
  state: DrawingHistoryState<T>;
  entry: DrawingHistoryEntry<T> | null;
}

export function createDrawingHistoryState<T = unknown>(
  limit = 100,
): DrawingHistoryState<T> {
  return {
    undoStack: [],
    redoStack: [],
    limit: Math.max(1, Math.floor(limit) || 100),
    suppressed: false,
  };
}

export function pushDrawingHistory<T>(
  state: DrawingHistoryState<T>,
  entry: DrawingHistoryEntry<T>,
): DrawingHistoryState<T> {
  if (state.suppressed) {
    return state;
  }

  const nextUndo = [...state.undoStack, entry];
  const trimmedUndo =
    nextUndo.length > state.limit
      ? nextUndo.slice(nextUndo.length - state.limit)
      : nextUndo;

  return {
    ...state,
    undoStack: trimmedUndo,
    redoStack: [],
  };
}

export function undoDrawingHistory<T>(
  state: DrawingHistoryState<T>,
): DrawingHistoryMutation<T> {
  const entry = state.undoStack[state.undoStack.length - 1] ?? null;
  if (!entry) {
    return { state, entry: null };
  }

  return {
    entry,
    state: {
      ...state,
      undoStack: state.undoStack.slice(0, -1),
      redoStack: [...state.redoStack, entry],
    },
  };
}

export function redoDrawingHistory<T>(
  state: DrawingHistoryState<T>,
): DrawingHistoryMutation<T> {
  const entry = state.redoStack[state.redoStack.length - 1] ?? null;
  if (!entry) {
    return { state, entry: null };
  }

  return {
    entry,
    state: {
      ...state,
      undoStack: [...state.undoStack, entry],
      redoStack: state.redoStack.slice(0, -1),
    },
  };
}

export function withSuppressedDrawingHistory<T>(
  state: DrawingHistoryState<T>,
  fn: (draft: DrawingHistoryState<T>) => DrawingHistoryState<T>,
): DrawingHistoryState<T> {
  const draft = { ...state, suppressed: true };
  const next = fn(draft);
  return {
    ...next,
    suppressed: state.suppressed,
  };
}

let historyState = createDrawingHistoryState<any>(50);

function notifyHistoryChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("lytick:drawing-history-changed"));
  }
}

export function pushHistory<T = unknown>(entry: DrawingHistoryEntry<T>) {
  historyState = pushDrawingHistory(historyState, entry);
  notifyHistoryChange();
}

export function undo<T = unknown>() {
  const result = undoDrawingHistory<T>(historyState);
  historyState = result.state;
  notifyHistoryChange();
  return result.entry;
}

export function redo<T = unknown>() {
  const result = redoDrawingHistory<T>(historyState);
  historyState = result.state;
  notifyHistoryChange();
  return result.entry;
}

export function canUndo() {
  return historyState.undoStack.length > 0;
}

export function canRedo() {
  return historyState.redoStack.length > 0;
}

export function clearHistory() {
  historyState = createDrawingHistoryState(50);
  notifyHistoryChange();
}

export function withHistorySuppressed<T>(fn: () => T): T {
  const previous = historyState.suppressed;
  historyState = {
    ...historyState,
    suppressed: true,
  };

  try {
    return fn();
  } finally {
    historyState = {
      ...historyState,
      suppressed: previous,
    };
  }
}
